import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  getAdvisorsAdminControllerListarQueryKey,
  useAdvisorsAdminControllerConfigurar,
} from '@/api/generated/admin-advisors/admin-advisors';
import type { AdvisorAdminResponse, AdvisorAdminResponseDiasDisponiveisItem } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { DIAS_EM_ORDEM, ROTULO_DIA } from '@/shared/lib/diasDaSemana';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import { toast } from '@/shared/ui/sonner';

/** Mesmo teto que `devolver()` usa no backend — capacidade configurada não pode passar disso. */
const CAPACIDADE_MAXIMA = 10;

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  advisor: AdvisorAdminResponse;
}

/**
 * Dias disponíveis + capacidade semanal de um advisor — aplica na hora, tanto
 * no saldo ATUAL quanto no teto usado pela renovação automática de domingo.
 */
export function AdvisorConfiguracaoDialogo({ aberto, aoFechar, advisor }: Props) {
  const queryClient = useQueryClient();
  const configurar = useAdvisorsAdminControllerConfigurar();

  const [dias, setDias] = useState<Set<AdvisorAdminResponseDiasDisponiveisItem>>(new Set());
  const [capacidade, setCapacidade] = useState(0);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (aberto) {
      setDias(new Set(advisor.diasDisponiveis));
      setCapacidade(advisor.capacidadeSemanal);
      setErro(null);
    }
  }, [aberto, advisor]);

  function alternarDia(dia: AdvisorAdminResponseDiasDisponiveisItem) {
    setDias((atual) => {
      const novo = new Set(atual);
      if (novo.has(dia)) novo.delete(dia);
      else novo.add(dia);
      return novo;
    });
  }

  const semDiaSelecionado = dias.size === 0;
  const houveMudanca =
    capacidade !== advisor.capacidadeSemanal ||
    dias.size !== advisor.diasDisponiveis.length ||
    !advisor.diasDisponiveis.every((d) => dias.has(d));

  async function salvar() {
    if (semDiaSelecionado) {
      setErro('Selecione ao menos um dia.');
      return;
    }
    setErro(null);

    try {
      await configurar.mutateAsync({
        id: advisor.id,
        data: { diasDisponiveis: [...dias], capacidadeSemanal: capacidade },
      });
      await queryClient.invalidateQueries({ queryKey: getAdvisorsAdminControllerListarQueryKey() });
      toast.success(`Configuração de ${advisor.nome} atualizada.`);
      aoFechar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não foi possível salvar a configuração.');
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Configurar {advisor.nome}</DialogTitle>
          <DialogDescription>
            Só nos dias marcados como disponíveis, e com saldo maior que zero, este advisor aparece
            para o médico escolher ao abrir uma nova discussão. Discussões já abertas não são
            afetadas.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {erro && (
            <Alert variant="destructive">
              <AlertDescription>{erro}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label className="text-brand font-medium">Dias disponíveis</Label>
            <div className="space-y-2">
              {DIAS_EM_ORDEM.map((dia) => (
                <div key={dia} className="flex items-center gap-2">
                  <Checkbox
                    id={`dia-${dia}`}
                    checked={dias.has(dia)}
                    onCheckedChange={() => alternarDia(dia)}
                  />
                  <Label htmlFor={`dia-${dia}`} className="font-normal cursor-pointer">
                    {ROTULO_DIA[dia]}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-brand font-medium">Capacidade semanal</Label>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setCapacidade((c) => Math.max(0, c - 1))}
                disabled={capacidade <= 0}
              >
                −
              </Button>
              <span className="w-10 text-center text-lg font-semibold text-foreground">
                {capacidade}
              </span>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setCapacidade((c) => Math.min(CAPACIDADE_MAXIMA, c + 1))}
                disabled={capacidade >= CAPACIDADE_MAXIMA}
              >
                +
              </Button>
              <span className="text-xs text-muted-foreground">até {CAPACIDADE_MAXIMA} vagas</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Aplica no saldo atual do advisor imediatamente, e vira o novo teto da renovação
              automática de domingo.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={aoFechar} disabled={configurar.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => void salvar()}
            disabled={configurar.isPending || semDiaSelecionado || !houveMudanca}
          >
            {configurar.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
